import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { GenericTable, UserActionsMenu } from '@components';
import type { UserActionsMenuOption } from '@components';
import { PvxPartnerTableRow, usePvxPartnersTable } from '../hooks/usePvxPartnersTable';
import { getPvxPartnersTableColumns } from '../utils/pvxPartnersTableColumns';
import AssignOwnerRoleModal from '../../../Dashboard/components/ManageUsers/components/Modals/AssignOwnerRoleModal';
import { CLIENT_USER_ROLE, CLIENT_USER_STATUS } from '@api/types/client-user-management';
import { RemoveAccessModal } from '../../../Dashboard/components/ManageUsers/components/Modals';
import { genericTexts } from '@utils/translations/en';

const TABLE_META = { chartType: 'MULTI_LEVEL_TABLE' };

type PvxPartnersTableProps = {
  pageNumber: number;
  pageSize: number;
  searchQuery: string;
  onPageReset: () => void;
  onTotalChange: (total: number) => void;
  onEdit: (row: PvxPartnerTableRow) => void;
};

export const PvxPartnersTable = ({
  pageNumber,
  pageSize,
  searchQuery,
  onPageReset,
  onTotalChange,
  onEdit,
}: PvxPartnersTableProps) => {
  const {
    rows,
    isLoading,
    total,
    sort,
    sortOrder,
    handleSort,
    roleFilter,
    handleRoleFilterChange,
    statusFilter,
    handleStatusFilterChange,
    typeFilter,
    handleTypeFilterChange,
    refetch,
  } = usePvxPartnersTable({ pageNumber, pageSize, searchQuery, onPageReset });

  useEffect(() => {
    onTotalChange(total);
  }, [total, onTotalChange]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<PvxPartnerTableRow | null>(null);
  const [showAssignOwnerModal, setShowAssignOwnerModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: PvxPartnerTableRow) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUser(row);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const closeModal = () => setShowDeleteConfirmation(false);

  const handleOnSuccess = () => {
    refetch();
    closeModal();
  };

  const menuOptions: UserActionsMenuOption[] = selectedUser
    ? [
        ...(selectedUser.role !== CLIENT_USER_ROLE.OWNER
          ? [
              {
                label: genericTexts.makeOwner,
                onClick: () => {
                  handleMenuClose();
                  setShowAssignOwnerModal(true);
                },
              },
            ]
          : []),
        {
          label: genericTexts.removeAccess,
          isDestructive: true,
          onClick: () => {
            handleMenuClose();
            setShowDeleteConfirmation(true);
          },
        },
      ]
    : [];

  const columns = useMemo(
    () =>
      getPvxPartnersTableColumns({
        onActionClick: handleMenuOpen,
        onEditCompany: onEdit,
        sort,
        sortOrder,
        onSort: handleSort,
        roleFilter,
        onRoleFilterChange: handleRoleFilterChange,
        statusFilter,
        onStatusFilterChange: handleStatusFilterChange,
        typeFilter,
        onTypeFilterChange: handleTypeFilterChange,
      }),
    [
      sort,
      sortOrder,
      handleSort,
      onEdit,
      roleFilter,
      handleRoleFilterChange,
      statusFilter,
      handleStatusFilterChange,
      typeFilter,
      handleTypeFilterChange,
    ],
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {selectedUser?.membershipGrantId && (
        <UserActionsMenu
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          options={menuOptions}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        />
      )}

      <GenericTable<PvxPartnerTableRow>
        data={rows}
        columns={columns}
        meta={TABLE_META}
        depth={1}
        hideExpandIfNoChildren
        isLoading={isLoading}
      />
      {showAssignOwnerModal && selectedUser?.membershipGrantId && selectedUser?.clientCode && (
        <AssignOwnerRoleModal
          open={showAssignOwnerModal}
          clientCode={selectedUser.clientCode}
          selectedUserEmail={selectedUser.user}
          selectedUserMembershipGrantId={selectedUser.membershipGrantId}
          onClose={() => {
            setShowAssignOwnerModal(false);
            setSelectedUser(null);
          }}
          onSubmit={refetch}
        />
      )}
      {showDeleteConfirmation && selectedUser?.membershipGrantId && selectedUser?.clientCode && (
        <RemoveAccessModal
          open
          clientCode={selectedUser.clientCode}
          selectedUserEmail={selectedUser.user}
          selectedUserStatus={selectedUser.status as CLIENT_USER_STATUS}
          selectedUserMembershipGrantId={selectedUser.membershipGrantId}
          onClose={closeModal}
          onSubmit={handleOnSuccess}
        />
      )}
    </Box>
  );
};
